import FormControl from '@material-ui/core/FormControl';
import MuiFormControlLabel from '@material-ui/core/FormControlLabel';
import MuiFormLabel from '@material-ui/core/FormLabel';
import MuiSwitch from '@material-ui/core/Switch';
import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';
import { focusOutline } from '../styles/focus';
import { bodyMedium, bodyRegular } from '../styles/text';
import { ConditionalWrapper } from './ConditionalWrapper';
import { HelperText } from './styled/form';
import { VisuallyHidden } from './VisuallyHidden';

const FormControlLabel = styled(MuiFormControlLabel)<{
  $isCompact?: boolean;
}>`
  ${({ $isCompact }) =>
    $isCompact &&
    css`
      &.MuiFormControlLabel-root {
        margin-right: 0;
        margin-left: 0;
        min-width: 3rem;
        justify-content: center;
      }
    `};

  & .MuiFormControlLabel-label {
    ${bodyRegular}
  }
`;

const StyledSwitch = styled(MuiSwitch).attrs({ disableRipple: true })`
  & .Mui-focusVisible .MuiSwitch-thumb {
    ${focusOutline({ $outlineOffset: '2px ' })}
  }

  & .Mui-checked {
    color: ${({ theme }) => theme.colors.builderBlue};

    &.Mui-disabled {
      color: ${({ theme }) => theme.colors.gray999};
    }

    &:hover {
      background-color: ${({ theme }) => theme.colors.builderBlue08};
    }

    & + .MuiSwitch-track {
      background-color: ${({ theme }) => theme.colors.builderBlue};
    }

    &.Mui-disabled + .MuiSwitch-track {
      background-color: ${({ theme }) => theme.colors.gray999};
    }
  }
` as typeof MuiSwitch;

const FormLabel = styled(MuiFormLabel)`
  ${bodyMedium}
  color: ${({ theme }) => theme.colors.gray333};
  transform: none;

  &.Mui-focused {
    color: ${({ theme }) => theme.colors.gray333};
  }
` as typeof MuiFormLabel;

type SwitchProps = React.ComponentProps<typeof MuiSwitch> & {
  checkedLabel?: string;
  helperText?: string;
  label?: string;
  uncheckedLabel?: string;
  isLabelHidden?: boolean;
  isErrorTextHidden?: boolean;
  isCompact?: boolean;
  isSubtextHidden?: boolean;
};

export function Switch({
  checked,
  checkedLabel = 'Yes',
  helperText,
  label,
  uncheckedLabel = 'No',
  isLabelHidden,
  className,
  isErrorTextHidden,
  isCompact,
  isSubtextHidden,
  ...props
}: SwitchProps) {
  const switchValueText = useMemo(
    () => (checked ? checkedLabel : uncheckedLabel),
    [checked, checkedLabel, uncheckedLabel],
  );
  return (
    <FormControl component="fieldset" className={className}>
      <ConditionalWrapper
        condition={!!isLabelHidden}
        /* eslint-disable-next-line react/no-unstable-nested-components */
        wrapper={(children) => <VisuallyHidden>{children}</VisuallyHidden>}
      >
        {label && <FormLabel component="legend">{label}</FormLabel>}
      </ConditionalWrapper>
      <FormControlLabel
        label={isLabelHidden ? null : switchValueText}
        control={<StyledSwitch checked={checked} {...props} />}
        $isCompact={isCompact}
      />
      <ConditionalWrapper
        condition={!!isErrorTextHidden}
        /* eslint-disable-next-line react/no-unstable-nested-components */
        wrapper={(children) => <VisuallyHidden>{children}</VisuallyHidden>}
      >
        <HelperText>{(!isSubtextHidden && helperText) || ' '}</HelperText>
      </ConditionalWrapper>
    </FormControl>
  );
}
